import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Icon from "@mdi/react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
} from "@mui/material";
import * as yup from "yup";

import CircularLoader from "components/CircularLoader";

export interface SetModal {
  title: string;
  body: string;
  onSubmit: (newName: string) => Promise<void>;
}

export interface SetInput {
  label: string;
  icon: string;
  value: string;
  type: string;
  validationSchema: yup.ObjectSchema<{ inputValue: string }> | null;
}

interface FormData {
  inputValue: string;
}

interface EditUserDataDialogProps {
  input: SetInput;
  modal: SetModal;
  onClose: () => void;
  open: boolean;
}

function EditUserDataDialog(props: EditUserDataDialogProps) {
  const { input, modal, onClose, open } = props;
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: { inputValue: input.value ?? "" },
    resolver: input.validationSchema ? yupResolver(input.validationSchema) : undefined,
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    await modal.onSubmit(data.inputValue);
    setLoading(false);

    onClose();
  };

  useEffect(() => {
    if (open) {
      reset({ inputValue: input.value });
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='edit-user-data-dialog' maxWidth='xs' fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
        <DialogTitle id='edit-user-data-dialog'>{modal.title}</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>{modal.body}</DialogContentText>
          <Controller
            control={control}
            name='inputValue'
            render={({ field }) => (
              <TextField
                {...field}
                label={input.label}
                type={input.type}
                margin='dense'
                size='small'
                error={Boolean(errors.inputValue)}
                helperText={errors.inputValue?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon path={input.icon} size={1} />
                    </InputAdornment>
                  ),
                }}
                autoFocus
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <CircularLoader loading={loading}>
            <Button color='primary' disabled={loading} type='submit'>
              Save
            </Button>
          </CircularLoader>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditUserDataDialog;
